import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { formatDate, formatDateTime } from "@/shared/lib/date-time";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Textarea,
  toast,
} from "@devhop/ui";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import {
  AlertTriangleIcon,
  ArrowLeftIcon,
  CheckIcon,
  DollarSignIcon,
  FileTextIcon,
  HistoryIcon,
  InfoIcon,
  PackageIcon,
  SendIcon,
  UserIcon,
  XIcon,
} from "lucide-react";
import { useState } from "react";
import {
  useApproveRefundRequestMutation,
  useExecuteRefundMutation,
  useRefundRequestQuery,
  useRejectRefundRequestMutation,
} from "../api/queries";

const STATUS_LABELS: Record<string, string> = {
  requested: "ລໍຖ້າກວດສອບ",
  approved: "ອະນຸມັດແລ້ວ",
  rejected: "ປະຕິເສດ",
  refunded: "ຄືນເງິນແລ້ວ",
  closed: "ປິດ",
};

const STATUS_BADGE_VARIANTS: Record<
  string,
  "default" | "secondary" | "destructive" | "outline"
> = {
  requested: "outline",
  approved: "default",
  rejected: "destructive",
  refunded: "secondary",
  closed: "secondary",
};

const PACKAGE_STATUS_LABELS: Record<string, string> = {
  active: "ໃຊ້ງານ",
  expired: "ໝົດອາຍຸ",
  used_up: "ໃຊ້ຄົບ",
  pending_cancel: "ລໍຖ້າຍົກເລີກ",
  canceled: "ຍົກເລີກແລ້ວ",
};

export function RefundRequestDetailPage() {
  const { id } = useParams({ from: "/app/refunds/$id" });
  const nav = useNavigate({ from: "/app/refunds/$id" });
  const { data: request, isLoading } = useRefundRequestQuery(id ?? null);
  const approveMutation = useApproveRefundRequestMutation(id ?? "");
  const rejectMutation = useRejectRefundRequestMutation(id ?? "");
  const executeMutation = useExecuteRefundMutation(id ?? "");
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");

  const handleApprove = async () => {
    try {
      await approveMutation.mutateAsync();
      toast.success("ອະນຸມັດຄຳຂໍຄືນເງິນສຳເລັດ");
    } catch {
      toast.error("ອະນຸມັດລົ້ມເຫຼວ");
    }
  };

  const handleReject = async () => {
    try {
      await rejectMutation.mutateAsync({ rejectionReason });
      setRejectDialogOpen(false);
      setRejectionReason("");
    } catch {
      toast.error("ປະຕິເສດລົ້ມເຫຼວ");
    }
  };

  const handleExecute = async () => {
    try {
      await executeMutation.mutateAsync();
      toast.success("ບັນທຶກການຄືນເງິນສຳເລັດ");
    } catch {
      toast.error("ບັນທຶກການຄືນເງິນລົ້ມເຫຼວ");
    }
  };

  const refundDetailBreadcrumb = (
    <Breadcrumb
      items={[
        { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
        { label: "ຄຳຂໍຄືນເງິນ", to: "/app/refunds" },
        { label: "ລາຍລະອຽດ" },
      ]}
    />
  );

  if (isLoading && !request) {
    return (
      <>
        <Header>{refundDetailBreadcrumb}</Header>
        <Main>
          <div className="flex items-center justify-center py-24">
            <div className="text-muted-foreground">ກຳລັງໂຫຼດ...</div>
          </div>
        </Main>
      </>
    );
  }

  if (!request) {
    return (
      <>
        <Header>{refundDetailBreadcrumb}</Header>
        <Main>
          <p className="py-12 text-center text-muted-foreground">ບໍ່ພົບຄຳຂໍຄືນເງິນ</p>
          <div className="flex justify-center">
            <Button
              variant="outline"
              onClick={() => nav({ to: "/app/refunds" })}
            >
              ກັບໄປລາຍການ
            </Button>
          </div>
        </Main>
      </>
    );
  }

  const canApprove = request.status === "requested";
  const canReject = request.status === "requested";
  const canExecute = request.status === "approved";

  const processingFee = Math.round(request.originalPrice * 0.1);
  const feeNote =
    processingFee > 0
      ? `* ຄ່າດຳເນີນການ 10% ຕາມນະໂຍບາຍ (${processingFee.toLocaleString("lo-LA")} ກີບ)`
      : null;

  return (
    <>
      <Header>{refundDetailBreadcrumb}</Header>

      <Main>
        {/* Page Title & Status */}
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="font-bold text-2xl">ລາຍລະອຽດຄຳຂໍຄືນເງິນ</h1>
              <Badge
                variant={STATUS_BADGE_VARIANTS[request.status] ?? "outline"}
                className="shrink-0"
              >
                {STATUS_LABELS[request.status] ?? request.status}
              </Badge>
            </div>
            <p className="mt-1 font-mono text-muted-foreground text-sm">
              ເລກທີ: #REF-{request.id.slice(0, 8).toUpperCase()}
            </p>
          </div>
          <Button variant="outline" onClick={() => nav({ to: "/app/refunds" })}>
            <ArrowLeftIcon className="size-4" />
            ກັບໄປລາຍການ
          </Button>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Left Column - Main Content */}
          <div className="space-y-6 lg:col-span-2">
            {/* Student & Package Information */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 font-normal text-muted-foreground text-sm">
                    <UserIcon className="size-4" />
                    ຂໍ້ມູນນັກຮຽນ ແລະ ແພັກເກດ
                  </CardTitle>
                  <Link
                    to="/app/students/$id"
                    params={{ id: request.studentId }}
                    className="text-primary text-sm hover:underline"
                  >
                    ເບິ່ງປະຫວັດ
                  </Link>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div className="space-y-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                      <UserIcon className="size-6 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-muted-foreground text-xs uppercase">
                        ຊື່ນັກຮຽນ
                      </p>
                      <p className="font-semibold">{request.studentName}</p>
                    </div>
                    <div>
                      <p className="font-medium text-muted-foreground text-xs uppercase">
                        ເລກທີ
                      </p>
                      <p className="font-semibold text-primary">
                        {request.memberId}
                      </p>
                    </div>
                    <div>
                      <p className="font-medium text-muted-foreground text-xs uppercase">
                        ເບີໂທ
                      </p>
                      <p className="font-semibold">
                        {request.phoneNumber ?? "-"}
                      </p>
                    </div>
                    <div>
                      <p className="font-medium text-muted-foreground text-xs uppercase">
                        ອີເມວ
                      </p>
                      <p className="font-semibold">{request.email ?? "-"}</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <PackageIcon className="size-5 text-primary" />
                      <span className="font-medium text-muted-foreground text-sm">
                        ແພັກເກດທີ່ຊື້
                      </span>
                    </div>
                    <p className="font-semibold">{request.packageName}</p>
                    <p className="text-muted-foreground text-sm">
                      ລົງທະບຽນ: {formatDate(request.enrolledAt)}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <Badge variant="secondary">
                        {PACKAGE_STATUS_LABELS[request.packageStatus] ??
                          request.packageStatus}
                      </Badge>
                      <Badge variant="outline">{request.targetLabel}</Badge>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Refund Details & Reason */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 font-normal text-muted-foreground text-sm">
                  <FileTextIcon className="size-4" />
                  ລາຍລະອຽດການຄືນເງິນ ແລະ ເຫດຜົນ
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div className="space-y-3">
                    <div>
                      <p className="font-medium text-muted-foreground text-xs uppercase">
                        ຍອດເງິນ
                      </p>
                      <p className="font-semibold">
                        {request.originalPrice.toLocaleString("lo-LA")} ກີບ
                      </p>
                    </div>
                    <div className="rounded-lg bg-primary/5 p-4">
                      <p className="font-medium text-muted-foreground text-xs uppercase">
                        ຍອດທີ່ຂໍຄືນເງິນ
                      </p>
                      <p className="font-bold text-primary text-xl">
                        {request.refundAmount.toLocaleString("lo-LA")} ກີບ
                      </p>
                    </div>
                    {feeNote && (
                      <p className="text-muted-foreground text-xs">{feeNote}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <p className="font-medium text-muted-foreground text-xs uppercase">
                      ເຫດຜົນຂໍຄືນເງິນ
                    </p>
                    <div className="rounded-lg border bg-muted/30 p-4">
                      <p className="whitespace-pre-wrap text-sm">
                        {request.reason}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Action Buttons */}
            {(canApprove || canReject || canExecute) && (
              <div className="flex flex-wrap gap-3">
                {canApprove && (
                  <Button
                    onClick={handleApprove}
                    disabled={approveMutation.isPending}
                  >
                    <CheckIcon className="mr-2 size-4" />
                    ອະນຸມັດ
                  </Button>
                )}
                {canReject && (
                  <Button
                    variant="destructive"
                    onClick={() => setRejectDialogOpen(true)}
                    disabled={rejectMutation.isPending}
                  >
                    <XIcon className="mr-2 size-4" />
                    ປະຕິເສດ
                  </Button>
                )}
                {canExecute && (
                  <Button
                    variant="secondary"
                    onClick={handleExecute}
                    disabled={executeMutation.isPending}
                  >
                    <DollarSignIcon className="mr-2 size-4" />
                    ຄືນເງິນແລ້ວ
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-6">
            {/* Approval Timeline */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 font-normal text-muted-foreground text-sm">
                  <HistoryIcon className="size-4" />
                  ປະຫວັດການອະນຸມັດ
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10">
                      <SendIcon className="size-4 text-primary" />
                    </div>
                    <div>
                      <p className="font-semibold text-sm">ສົ່ງຄຳຂໍແລ້ວ</p>
                      <p className="text-muted-foreground text-xs">
                        ໂດຍ: {request.requestedByName ?? "-"}
                      </p>
                      <p className="text-muted-foreground text-xs">
                        {formatDateTime(request.requestedAt)}
                      </p>
                    </div>
                  </div>

                  {request.status !== "requested" && request.approvedAt && (
                    <div className="flex gap-3">
                      <div
                        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
                          request.status === "approved"
                            ? "bg-green-500/10"
                            : "bg-destructive/10"
                        }`}
                      >
                        {request.status === "approved" ? (
                          <CheckIcon className="size-4 text-green-600" />
                        ) : (
                          <XIcon className="size-4 text-destructive" />
                        )}
                      </div>
                      <div>
                        <p className="font-semibold text-sm">
                          {request.status === "approved"
                            ? "ອະນຸມັດແລ້ວ"
                            : "ປະຕິເສດ"}
                        </p>
                        <p className="text-muted-foreground text-xs">
                          ໂດຍ: {request.approvedByName ?? "-"}
                        </p>
                        <p className="text-muted-foreground text-xs">
                          {formatDateTime(request.approvedAt)}
                        </p>
                        {request.rejectionReason && (
                          <p className="mt-1 text-muted-foreground text-xs">
                            ເຫດຜົນ: {request.rejectionReason}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {request.status === "refunded" && request.refundedAt && (
                    <div className="flex gap-3">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-green-500/10">
                        <DollarSignIcon className="size-4 text-green-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-sm">ຄືນເງິນແລ້ວ</p>
                        <p className="text-muted-foreground text-xs">
                          ໂດຍ: {request.refundedByName ?? "-"}
                        </p>
                        <p className="text-muted-foreground text-xs">
                          {formatDateTime(request.refundedAt)}
                        </p>
                      </div>
                    </div>
                  )}

                  {request.status === "requested" && (
                    <div className="flex gap-3 opacity-60">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted">
                        <FileTextIcon className="size-4 text-muted-foreground" />
                      </div>
                      <div>
                        <p className="font-medium text-muted-foreground text-sm">
                          ລໍຖ້າການອະນຸມັດ
                        </p>
                        <p className="text-muted-foreground text-xs">
                          ຍັງບໍ່ມີການດຳເນີນການ
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* System Notes */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 font-normal text-muted-foreground text-sm">
                  <InfoIcon className="size-4" />
                  ໝາຍເຫດຂອງລະບົບ
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm">
                  ການຄືນເງິນປົກກະຕິໃຊ້ເວລາ 3-5 ວັນທຳມະດາຫຼັງຈາກອະນຸມັດ.
                  ເງິນຈະຖືກໂອນໄປບັນຊີທີ່ນັກຮຽນໃຫ້ມາຢູ່ໃນລະບົບ.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </Main>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangleIcon className="size-5 text-destructive" />
              ເຫດຜົນການປະຕິເສດ
            </DialogTitle>
          </DialogHeader>
          <p className="text-muted-foreground text-sm">
            ກະລຸນາໃຫ້ຂໍ້ມູນທີ່ຊັດເຈນເພື່ອແຈ້ງເຕືອນຜູ້ຂໍ
          </p>
          <Textarea
            placeholder="ປ້ອນເຫດຜົນການປະຕິເສດທີ່ນີ້..."
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            rows={4}
            className="resize-none"
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setRejectDialogOpen(false);
                setRejectionReason("");
              }}
            >
              ຍົກເລີກ
            </Button>
            <Button
              variant="destructive"
              onClick={handleReject}
              disabled={rejectMutation.isPending}
            >
              ຢືນຢັນການປະຕິເສດ
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
